import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isSysAdmin } from 'modules/auth/selectors';
import dayjs from 'dayjs';
import AnalyticsTable, {
    AnalyticsSelectFilter,
    AnalyticsRangeFilter,
} from 'components/AnalyticsTable';
import { applyFilters, applySorting, paginate } from 'components/AnalyticsTable/dataUtils';
import { calcPctChange, buildPctChangeColumn } from 'components/AnalyticsTable/PctChangeColumn';
import { getUsageAnalyticsMonthly } from 'modules/api/usageAnalytics';
import ComparisonDialog from './ComparisonDialog';

function getUnique(data, key) {
    return [...new Set(data.map((r) => r[key]).filter((v) => v != null && v !== ''))].sort();
}

function sel(accessor, Header, data) {
    return {
        accessor,
        Header,
        Filter: AnalyticsSelectFilter,
        filterOptions: data ? getUnique(data, accessor) : [],
    };
}

function buildMomColumn(compareLookup, onMomClick) {
    return buildPctChangeColumn({
        id: 'momChange',
        header: 'Pokytis, %',
        accessor: (row) => {
            if (row.service !== 'API') return null;
            const prev = compareLookup[getRowKey(row)];
            if (!prev?.quantity) return null;
            return calcPctChange(row.quantity, prev.quantity);
        },
        cellProps: (row) => ({
            onClick: () => onMomClick(row.original.callerIdentifier),
            style: { cursor: 'pointer', textDecoration: 'underline' },
        }),
    });
}

function buildColumns(data, showDealer, compareLookup, onMomClick) {
    return [
        sel('service', 'Paslauga', data),
        sel('clientName', 'Klientas', data),
        ...(showDealer ? [sel('dealerName', 'Atstovas', data)] : []),
        sel('clientDbName', 'Duomenų bazė', data),
        sel('clientDbCompanyName', 'DB įmonė', data),
        sel('callerIdentifier', 'Identifikatorius', data),
        {
            accessor: 'identifierCreatedAt',
            Header: 'Sukūrimo data',
            disableFilters: true,
            width: 120,
            Cell: ({ cell: { value } }) => (value ? dayjs(value).format('YYYY-MM-DD') : '–'),
        },
        sel('designation', 'Paskirtis', data),
        { accessor: 'quantity', Header: 'Kiekis', Filter: AnalyticsRangeFilter, width: 120 },
        ...(compareLookup ? [buildMomColumn(compareLookup, onMomClick)] : []),
    ];
}

const getRowKey = (row) =>
    [row.service, row.callerIdentifier, row.clientDbId]
        .map((v) => v ?? '')
        .join('\0');

const getRowID = getRowKey;

function buildCompareLookup(data) {
    const lookup = {};
    for (const row of data) {
        lookup[getRowKey(row)] = row;
    }
    return lookup;
}

function getMomPct(row, compareLookup) {
    if (!compareLookup || row.service !== 'API') return null;
    const prev = compareLookup[getRowKey(row)];
    if (!prev?.quantity) return null;
    return calcPctChange(row.quantity, prev.quantity);
}

export default function UsageDetailTable({ monthStart, compareMonth, onFiltersChange }) {
    const cache = useRef({ key: null, data: null });
    const [fullData, setFullData] = useState(null);
    const [compareLookup, setCompareLookup] = useState(null);
    const [dialog, setDialog] = useState({ open: false, identifier: null });
    const showDealer = useSelector(isSysAdmin);

    useEffect(() => {
        if (!compareMonth) {
            setCompareLookup(null);
            return;
        }
        getUsageAnalyticsMonthly(compareMonth).then((data) => {
            setCompareLookup(buildCompareLookup(data));
        });
    }, [compareMonth]);

    const handleMomClick = useCallback((identifier) => {
        setDialog({ open: true, identifier });
    }, []);

    const columns = useMemo(
        () => buildColumns(fullData, showDealer, compareLookup, handleMomClick),
        [fullData, showDealer, compareLookup, handleMomClick],
    );

    const loadData = useCallback(
        (query, rawFilters) => {
            const cacheKey = monthStart;
            const cached = cache.current.key === cacheKey ? cache.current.data : null;

            const fetchPromise = cached
                ? Promise.resolve(cached)
                : getUsageAnalyticsMonthly(monthStart).then((data) => {
                      cache.current = { key: cacheKey, data };
                      setFullData(data);
                      return data;
                  });

            return fetchPromise.then((allData) => {
                let filtered = rawFilters?.length ? applyFilters(allData, rawFilters) : allData;
                filtered = applySorting(filtered, query.sort, (row, field) =>
                    field === 'momChange' ? getMomPct(row, compareLookup) ?? -Infinity : undefined,
                );
                return paginate(filtered, query.page, query.pageSize);
            });
        },
        [monthStart, compareLookup],
    );

    return (
        <>
            <AnalyticsTable
                columns={columns}
                loadData={loadData}
                getRowID={getRowID}
                onFiltersChange={onFiltersChange}
                pageSizeOptions={[50, 100, 200]}
                pageSizeStorageKey="usageDetailPageSize"
            />
            <ComparisonDialog
                open={dialog.open}
                onClose={() => setDialog({ open: false, identifier: null })}
                identifier={dialog.identifier}
                monthStart={monthStart}
                compareMonth={compareMonth}
            />
        </>
    );
}
